import {
  readAllowScriptsPreference,
  readAnimationTimelineOpenPreference,
  readAnimationPreviewWindowOpenPreference,
  readScriptsDebugWindowOpenPreference,
  readShowRulersPreference,
  readSnapToGuidesPreference,
  readSculptBrushPreference,
  writeAllowScriptsPreference,
  writeAnimationTimelineOpenPreference,
  writeAnimationPreviewWindowOpenPreference,
  writeScriptsDebugWindowOpenPreference,
  writeShowRulersPreference,
  writeSnapToGuidesPreference,
  writeSculptBrushPreference,
} from "@/editor/shell/editorPreferences";
import { DEFAULT_ANIMATION_TIMELINE_VIEWPORT } from "@/editor/shell/AnimationTimeline";
import { StoreSubject } from "@/lib/store/StoreSubject";
import {
  type AnimationUiState,
  type CanvasDebugState,
  type CanvasSnapState,
  type ComponentImportProgressState,
  type ContextMenuState,
  type FillEditTarget,
  type RightPanelTab,
  type ScriptDebugLogEntry,
  type ScriptParamPreviewUiState,
  type ScriptPreviewUiState,
  type SculptBrushState,
  type SelectionPaintPickerRequest,
  type SubdivideSessionState,
  type TextSelectionState,
  type UiChromeMode,
} from "./editorStoreTypes";
import type { ColorSamplerRequest } from "@/editor/shell/colorSampler";
import type { CanvasPointerStatus } from "@/editor/shell/canvasWorkspaceTypes";
import type { GuideSelection } from "@/document/model/guides";

function animationUiEqual(left: AnimationUiState, right: AnimationUiState): boolean {
  return left.panelOpen === right.panelOpen
    && left.timelineOpen === right.timelineOpen
    && left.previewWindowOpen === right.previewWindowOpen
    && left.playing === right.playing
    && left.timeMs === right.timeMs
    && left.activeRootId === right.activeRootId
    && left.armedNodeKind === right.armedNodeKind
    && left.exportingFormat === right.exportingFormat
    && left.loopRegion?.startMs === right.loopRegion?.startMs
    && left.loopRegion?.endMs === right.loopRegion?.endMs
    && left.vectorTargetNodeId === right.vectorTargetNodeId
    && JSON.stringify(left.timelineViewports) === JSON.stringify(right.timelineViewports);
}

function canvasDebugEqual(left: CanvasDebugState, right: CanvasDebugState): boolean {
  return left.showPixelGrid === right.showPixelGrid
    && left.showRulers === right.showRulers
    && left.showRenderDebug === right.showRenderDebug
    && left.renderTriangles === right.renderTriangles
    && left.showTileGaps === right.showTileGaps
    && left.useWasmTessellation === right.useWasmTessellation;
}

function canvasSnapEqual(left: CanvasSnapState, right: CanvasSnapState): boolean {
  return left.snapToPixelGrid === right.snapToPixelGrid
    && left.snapToObjects === right.snapToObjects
    && left.snapToGuides === right.snapToGuides
    && left.snapToGeometry === right.snapToGeometry
    && left.assistedPencilTracing === right.assistedPencilTracing;
}

function createDefaultAnimationUiState(): AnimationUiState {
  return {
    panelOpen: false,
    timelineOpen: readAnimationTimelineOpenPreference(),
    previewWindowOpen: readAnimationPreviewWindowOpenPreference(),
    playing: false,
    timeMs: 0,
    timelineViewports: {},
  };
}

function createDefaultCanvasDebugState(): CanvasDebugState {
  return {
    showPixelGrid: true,
    showRulers: readShowRulersPreference(),
    showRenderDebug: false,
    renderTriangles: false,
    showTileGaps: false,
    useWasmTessellation: true,
  };
}

function createDefaultCanvasSnapState(): CanvasSnapState {
  return {
    snapToPixelGrid: true,
    snapToObjects: true,
    snapToGuides: readSnapToGuidesPreference(),
    snapToGeometry: true,
    assistedPencilTracing: true,
  };
}

export class UiStore {
  readonly message = new StoreSubject("Ready");
  readonly isDocumentLoading = new StoreSubject(true);
  readonly zoom = new StoreSubject(1);
  readonly rightPanelTab = new StoreSubject<RightPanelTab>("design");
  readonly uiChromeMode = new StoreSubject<UiChromeMode>("visible");
  readonly allowScripts = new StoreSubject(readAllowScriptsPreference());
  readonly scriptEditorScriptId = new StoreSubject<string | undefined>(undefined);
  readonly scriptsDebugWindowOpen = new StoreSubject(readScriptsDebugWindowOpenPreference());
  readonly scriptDebugLogs = new StoreSubject<ScriptDebugLogEntry[]>([]);
  readonly scriptPreview = new StoreSubject<ScriptPreviewUiState | undefined>(undefined);
  readonly scriptParamPreview = new StoreSubject<ScriptParamPreviewUiState | undefined>(undefined);
  readonly contextMenu = new StoreSubject<ContextMenuState | undefined>(undefined);
  readonly pointerStatus = new StoreSubject<CanvasPointerStatus | undefined>(undefined);
  readonly sculptBrush = new StoreSubject<SculptBrushState>(readSculptBrushPreference());
  readonly subdivideSession = new StoreSubject<SubdivideSessionState | undefined>(undefined);
  readonly colorSampler = new StoreSubject<ColorSamplerRequest | undefined>(undefined);
  readonly selectedGuide = new StoreSubject<GuideSelection | undefined>(undefined);
  readonly selectedPrototypeLinkId = new StoreSubject<string | undefined>(undefined);
  readonly prototypePlayerFrameId = new StoreSubject<string | undefined>(undefined);
  readonly textSelection = new StoreSubject<TextSelectionState | undefined>(undefined);
  readonly editingImageFillTarget = new StoreSubject<FillEditTarget | undefined>(undefined);
  readonly editingGradientFillTarget = new StoreSubject<FillEditTarget | undefined>(undefined);
  readonly selectionPaintPicker = new StoreSubject<SelectionPaintPickerRequest | undefined>(undefined);
  readonly componentImportProgress = new StoreSubject<ComponentImportProgressState | undefined>(undefined);
  readonly animation = new StoreSubject<AnimationUiState>(createDefaultAnimationUiState(), {
    equals: animationUiEqual,
  });
  readonly canvasDebug = new StoreSubject<CanvasDebugState>(createDefaultCanvasDebugState(), {
    equals: canvasDebugEqual,
  });
  readonly canvasSnap = new StoreSubject<CanvasSnapState>(createDefaultCanvasSnapState(), {
    equals: canvasSnapEqual,
  });

  setMessage = (message: string): void => {
    this.message.next(message);
  };

  setDocumentLoading = (loading: boolean): void => {
    this.isDocumentLoading.next(loading);
  };

  setZoom = (zoom: number): void => {
    this.zoom.next(zoom);
  };

  setRightPanelTab = (tab: RightPanelTab): void => {
    this.rightPanelTab.next(tab);
  };

  setUiChromeMode = (mode: UiChromeMode): void => {
    this.uiChromeMode.next(mode);
  };

  setContextMenu = (menu: ContextMenuState | undefined): void => {
    this.contextMenu.next(menu);
  };

  setPointerStatus = (status: CanvasPointerStatus | undefined): void => {
    this.pointerStatus.next(status);
  };

  patchSculptBrush = (patch: Partial<SculptBrushState>): void => {
    const current = this.sculptBrush.getValue();
    const next = {
      size: Math.min(1000, Math.max(2, patch.size ?? current.size)),
      strength: Math.min(100, Math.max(1, patch.strength ?? current.strength)),
    };
    this.sculptBrush.next(next);
    writeSculptBrushPreference(next);
  };

  openSubdivideSession = (session: Omit<SubdivideSessionState, "token">): void => {
    const token = (this.subdivideSession.getValue()?.token ?? 0) + 1;
    this.subdivideSession.next({
      ...session,
      segments: Math.min(64, Math.max(2, Math.floor(session.segments))),
      edgeIds: [...session.edgeIds],
      token,
    });
  };

  patchSubdivideSession = (patch: Partial<Pick<SubdivideSessionState, "segments" | "connect" | "edgeIds">>): void => {
    const current = this.subdivideSession.getValue();
    if (!current) {
      return;
    }
    this.subdivideSession.next({
      ...current,
      segments: patch.segments !== undefined
        ? Math.min(64, Math.max(2, Math.floor(patch.segments)))
        : current.segments,
      connect: patch.connect ?? current.connect,
      edgeIds: patch.edgeIds ? [...patch.edgeIds] : current.edgeIds,
    });
  };

  closeSubdivideSession = (): void => {
    this.subdivideSession.next(undefined);
  };

  setColorSampler = (request: ColorSamplerRequest | undefined): void => {
    this.colorSampler.next(request);
  };

  openSelectionPaintPicker = (
    kind: SelectionPaintPickerRequest["kind"],
    nodeIds: string[],
    selectionNodeIds: string[],
    primaryNodeId?: string,
  ): void => {
    const token = (this.selectionPaintPicker.getValue()?.token ?? 0) + 1;
    this.selectionPaintPicker.next({
      kind,
      nodeIds: [...nodeIds],
      selectionNodeIds: [...selectionNodeIds],
      primaryNodeId,
      token,
    });
  };

  closeSelectionPaintPicker = (): void => {
    this.selectionPaintPicker.next(undefined);
  };

  updateColorSampler = (updater: (current: ColorSamplerRequest | undefined) => ColorSamplerRequest | undefined): void => {
    this.setColorSampler(updater(this.colorSampler.getValue()));
  };

  setSelectedGuide = (selection: GuideSelection | undefined): void => {
    this.selectedGuide.next(selection);
  };

  setSelectedPrototypeLinkId = (linkId: string | undefined): void => {
    this.selectedPrototypeLinkId.next(linkId);
  };

  setPrototypePlayerFrameId = (frameId: string | undefined): void => {
    this.prototypePlayerFrameId.next(frameId);
  };

  setTextSelection = (selection: TextSelectionState | undefined): void => {
    this.textSelection.next(selection);
  };

  setEditingImageFillTarget = (target: FillEditTarget | undefined): void => {
    this.editingImageFillTarget.next(target);
  };

  setEditingGradientFillTarget = (target: FillEditTarget | undefined): void => {
    this.editingGradientFillTarget.next(target);
  };

  setComponentImportProgress = (progress: ComponentImportProgressState | undefined): void => {
    this.componentImportProgress.next(progress);
  };

  patchAnimation = (patch: Partial<AnimationUiState>): void => {
    this.animation.next({ ...this.animation.getValue(), ...patch });
  };

  patchCanvasDebug = (patch: Partial<CanvasDebugState>): void => {
    this.canvasDebug.next({ ...this.canvasDebug.getValue(), ...patch });
  };

  patchCanvasSnap = (patch: Partial<CanvasSnapState>): void => {
    this.canvasSnap.next({ ...this.canvasSnap.getValue(), ...patch });
  };

  setShowRulers = (show: boolean): void => {
    writeShowRulersPreference(show);
    this.patchCanvasDebug({ showRulers: show });
  };

  setSnapToGuides = (snap: boolean): void => {
    writeSnapToGuidesPreference(snap);
    this.patchCanvasSnap({ snapToGuides: snap });
  };

  setAnimationPreviewWindowOpen = (open: boolean): void => {
    writeAnimationPreviewWindowOpenPreference(open);
    this.patchAnimation({ previewWindowOpen: open });
  };

  setAnimationTimelineOpen = (open: boolean): void => {
    writeAnimationTimelineOpenPreference(open);
    this.patchAnimation({ timelineOpen: open });
  };

  setAllowScripts = (allow: boolean): void => {
    writeAllowScriptsPreference(allow);
    this.allowScripts.next(allow);
    if (!allow) {
      this.scriptEditorScriptId.next(undefined);
      this.clearScriptPreview();
      this.clearScriptParamPreview();
    }
  };

  setScriptsDebugWindowOpen = (open: boolean): void => {
    writeScriptsDebugWindowOpenPreference(open);
    this.scriptsDebugWindowOpen.next(open);
  };

  openScriptPreview = (state: Omit<ScriptPreviewUiState, "revision"> & { revision?: number }): void => {
    this.scriptPreview.next({
      frameId: state.frameId,
      frameName: state.frameName,
      pages: state.pages,
      revision: state.revision ?? 1,
    });
  };

  updateScriptPreviewPages = (pages: ScriptPreviewUiState["pages"]): void => {
    const current = this.scriptPreview.getValue();
    if (!current) {
      return;
    }
    this.scriptPreview.next({
      ...current,
      pages,
      revision: current.revision + 1,
    });
  };

  clearScriptPreview = (): void => {
    this.scriptPreview.next(undefined);
  };

  openScriptParamPreview = (state: Omit<ScriptParamPreviewUiState, "revision"> & { revision?: number }): void => {
    this.scriptParamPreview.next({
      frameId: state.frameId,
      frameName: state.frameName,
      pages: state.pages,
      revision: state.revision ?? 1,
    });
  };

  updateScriptParamPreviewPages = (pages: ScriptParamPreviewUiState["pages"]): void => {
    const current = this.scriptParamPreview.getValue();
    if (!current) {
      return;
    }
    this.scriptParamPreview.next({
      ...current,
      pages,
      revision: current.revision + 1,
    });
  };

  clearScriptParamPreview = (): void => {
    this.scriptParamPreview.next(undefined);
  };

  appendScriptDebugLogs = (entries: Array<Omit<ScriptDebugLogEntry, "id" | "ts"> & { id?: string; ts?: number }>): void => {
    if (entries.length === 0) {
      return;
    }
    const now = Date.now();
    const next = entries.map((entry, index) => ({
      id: entry.id ?? `script-log-${now}-${index}-${Math.random().toString(36).slice(2, 8)}`,
      ts: entry.ts ?? now,
      level: entry.level,
      message: entry.message,
      source: entry.source,
    }));
    this.scriptDebugLogs.next([...this.scriptDebugLogs.getValue(), ...next]);
    if (next.some(entry => entry.level === "error" || entry.level === "info")) {
      this.setScriptsDebugWindowOpen(true);
    }
  };

  clearScriptDebugLogs = (): void => {
    this.scriptDebugLogs.next([]);
  };

  openScriptEditor = (scriptId: string): void => {
    if (!this.allowScripts.getValue()) {
      return;
    }
    this.scriptEditorScriptId.next(scriptId);
  };

  closeScriptEditor = (): void => {
    this.scriptEditorScriptId.next(undefined);
  };

  defaultAnimationTimelineViewportKey = (activeRootId: string | undefined): string => {
    return activeRootId ?? "__default__";
  };

  animationTimelineViewport = (activeRootId: string | undefined) => {
    const key = this.defaultAnimationTimelineViewportKey(activeRootId);
    return this.animation.getValue().timelineViewports[key] ?? DEFAULT_ANIMATION_TIMELINE_VIEWPORT;
  };
}

export const uiStore = new UiStore();
